import React, { useState } from 'react';
import styled from 'styled-components';
import { usuarioService, sessaoService } from '../services/api';
import { validarCPF } from '../utils/cpfValidator';

const maskCPF = (value: string): string => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const maskCelular = (value: string): string => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
};

const maskEmail = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9@._-]/g, '');
};

const maskAltura = (value: string): string => {
  let cleanValue = value.replace(/[^\d.]/g, '');
  
  if (cleanValue.length === 3 && !cleanValue.includes('.')) {
    cleanValue = cleanValue.slice(0, 1) + '.' + cleanValue.slice(1);
  }
  
  if (cleanValue.length === 4 && !cleanValue.includes('.')) {
    cleanValue = cleanValue.slice(0, 2) + '.' + cleanValue.slice(2);
  }
  
  if (cleanValue.includes('.')) {
    const parts = cleanValue.split('.');
    if (parts[1] && parts[1].length > 2) {
      cleanValue = parts[0] + '.' + parts[1].slice(0, 2);
    }
  }
  
  cleanValue = cleanValue.replace(/\.{2,}/g, '.');
  
  if (cleanValue.startsWith('.')) {
    cleanValue = '0' + cleanValue;
  }
  
  if (cleanValue.length > 1 && cleanValue.startsWith('0') && !cleanValue.startsWith('0.')) {
    cleanValue = cleanValue.replace(/^0+/, '');
  }
  
  return cleanValue;
};

interface DadosPessoaisProps {
  data: any;
  onChange: (field: string, value: any) => void;
  mapeamentoAnonimizado?: boolean;
  isDass21?: boolean;
  somenteTitulares?: boolean;
  pesquisaId?: string;
  onValidationError?: (errors: string[]) => void;
  onBlockNavigation?: (blocked: boolean) => void;
  onSessaoEncontrada?: (sessao: any) => void;
  onNext?: () => void;
  onUsuarioCriado?: (usuario: any) => void;
  onSessaoCriada?: (sessao: any) => void;
  validateBeforeSubmit?: () => string[];
}

const Container = styled.div`
  width: 100%;
  margin: 30px 0;
  background: #ffffff;
  border-radius: 12px;
  padding: 40px;
`;

const Title = styled.h3`
  width: 100%;
  text-align: left;
  margin-bottom: 30px;
  color: #222222;
  font-size: 24px;
  font-weight: 700;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 15px;
`;

const Subtitle = styled.h4`
  margin-bottom: 15px;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
`;

const Label = styled.label`
  font-size: 14px;
  color: #495057;
  margin-right: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Form = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 30px;
`;

const Note = styled.p`
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 15px;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #222222;
  line-height: 1.5;
`;

const Input = styled.input`
  line-height: 32px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  color: #495057;
  width: 100%;
  font-size: 16px;
  padding: 12px 16px;
  background: #fff;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #222222;
    outline: none;
    box-shadow: 0 0 0 3px rgba(34, 34, 34, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 8px 0;
  
  input[type="radio"] {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    accent-color: #222222;
  }
  
`;

const DadosPessoais: React.FC<DadosPessoaisProps> = ({ 
  data, 
  onChange, 
  mapeamentoAnonimizado = false,
  isDass21 = false,
  somenteTitulares = false,
  pesquisaId,
  onValidationError,
  onBlockNavigation,
  onSessaoEncontrada,
  onNext,
  onUsuarioCriado,
  onSessaoCriada,
  validateBeforeSubmit
}) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isCheckingCpf, setIsCheckingCpf] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const getSessaoFromLocalStorage = () => {
    try {
      const sessaoData = localStorage.getItem('sessao_pesquisa');
      if (sessaoData) {
        const parsed = JSON.parse(sessaoData);
        return parsed;
      }
    } catch (error) {
      console.error('Erro ao recuperar sessão do localStorage:', error);
    }
    return null;
  };

  const saveSessaoToLocalStorage = (sessaoData: any) => {
    try {
      localStorage.setItem('sessao_pesquisa', JSON.stringify(sessaoData));
    } catch (error) {
      console.error('Erro ao salvar sessão no localStorage:', error);
    }
  };

  const clearSessaoFromLocalStorage = () => {
    try {
      localStorage.removeItem('sessao_pesquisa');
    } catch (error) {
      console.error('Erro ao remover sessão do localStorage:', error);
    }
  };

  const getUsuariosFromLocalStorage = () => {
    try {
      const usuariosData = localStorage.getItem('usuarios_pesquisa');
      if (usuariosData) {
        const parsed = JSON.parse(usuariosData);
        return parsed;
      }
    } catch (error) {
      console.error('Erro ao recuperar usuários do localStorage:', error);
    }
    return null;
  };

  const saveUsuariosToLocalStorage = (usuariosData: any[]) => {
    try {
      localStorage.setItem('usuarios_pesquisa', JSON.stringify(usuariosData));
    } catch (error) {
      console.error('Erro ao salvar usuários no localStorage:', error);
    }
  };

  const clearUsuariosFromLocalStorage = () => {
    try {
      localStorage.removeItem('usuarios_pesquisa');
    } catch (error) {
      console.error('Erro ao remover usuários do localStorage:', error);
    }
  };

  const handleCreateSessionAndUser = async () => {
    if (validateBeforeSubmit) {
      const errors = validateBeforeSubmit();
      if (errors.length > 0) {
        setValidationErrors(errors);
        if (onValidationError) onValidationError(errors);
        return;
      }
    }
    try {
      setIsCreating(true);
      setValidationErrors([]);
      
      let sessaoCriada = getSessaoFromLocalStorage();
      
      if (!sessaoCriada || !sessaoCriada.id || sessaoCriada.id.startsWith('temp_')) {
        
        const dadosSessao = {
          status: 'pendente',
          ultima_pagina: 1,
          progresso_percentual: 0,
          tempo_total_segundos: 0,
          ip_address: window.location.hostname === 'localhost' ? '127.0.0.1' : window.location.hostname,
          user_agent: navigator.userAgent,
          dados_metadados: {
            resolucao: `${window.screen.width}x${window.screen.height}`,
            idioma: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        };

        
        sessaoCriada = await sessaoService.create({
          ...dadosSessao,
          pesquisa_id: pesquisaId
        });
        
        
        if (!sessaoCriada || !sessaoCriada.id) {
          throw new Error('Falha ao criar sessão: resposta inválida da API');
        }
        
        
        saveSessaoToLocalStorage(sessaoCriada);
        
        if (onSessaoCriada) {
          onSessaoCriada(sessaoCriada);
        }
      } else {
      }

      
      const numeroDependentes = data.numero_dependentes || 0;
      const usuariosCriados: any[] = [];
      
      const usuarioTitularParaEnvio: any = {
        sexo: data.sexo,
        nascimento: data.nascimento,
        numero_dependentes: numeroDependentes,
        tipo_usuario: 'titular'
      };

      if (!isDass21) {
        usuarioTitularParaEnvio.peso = data.peso;
        usuarioTitularParaEnvio.altura = data.altura;
      }
      
      if (!mapeamentoAnonimizado) {
        usuarioTitularParaEnvio.nome = data.nome;
        usuarioTitularParaEnvio.cpf = data.cpf;
        usuarioTitularParaEnvio.email = data.email;
        usuarioTitularParaEnvio.celular = data.celular;
      }
      
      
      Object.keys(usuarioTitularParaEnvio).forEach(key => {
        const value = (usuarioTitularParaEnvio as any)[key];
        if (key !== 'tipo_usuario' && (value === undefined || value === null || value === '')) {
          delete (usuarioTitularParaEnvio as any)[key];
        }
      });
      
      
      
      const dadosParaEnvio = {
        ...usuarioTitularParaEnvio,
        pesquisa_id: pesquisaId,
        sessao_id: sessaoCriada.id
      };
      
      
      const usuarioTitular = await usuarioService.create(dadosParaEnvio);
      
      if (!usuarioTitular || !usuarioTitular.id) {
        throw new Error('Falha ao criar usuário titular: resposta inválida da API');
      }
      
      usuariosCriados.push(usuarioTitular);
      
      if (numeroDependentes > 0) {
        
        for (let i = 1; i <= numeroDependentes; i++) {
          const dependenteData = {
            sexo: data[`dependente_${i}_sexo`],
            nascimento: data[`dependente_${i}_nascimento`],
            peso: data[`dependente_${i}_peso`],
            altura: data[`dependente_${i}_altura`],
            relacionamento: data[`dependente_${i}_relacionamento`],
            nome: data[`dependente_${i}_nome`],
            cpf: data[`dependente_${i}_cpf`]
          };
          
          const relacionamento = data[`dependente_${i}_relacionamento`];
          if (!relacionamento) {
            throw new Error(`Relacionamento é obrigatório para o dependente ${i}`);
          }

          const dependenteParaEnvio: any = {
            sexo: data[`dependente_${i}_sexo`],
            nascimento: data[`dependente_${i}_nascimento`],
            relacionamento: relacionamento,
            tipo_usuario: 'dependente'
          };

          if (!isDass21) {
            dependenteParaEnvio.peso = data[`dependente_${i}_peso`];
            dependenteParaEnvio.altura = data[`dependente_${i}_altura`];
          }
          
          if (!mapeamentoAnonimizado) {
            dependenteParaEnvio.nome = data[`dependente_${i}_nome`];
            dependenteParaEnvio.cpf = data[`dependente_${i}_cpf`];
          }
          
          Object.keys(dependenteParaEnvio).forEach(key => {
            const value = (dependenteParaEnvio as any)[key];
            if (key !== 'relacionamento' && key !== 'tipo_usuario' && (value === undefined || value === null || value === '')) {
              delete (dependenteParaEnvio as any)[key];
            }
          });
          
          
          const dependente = await usuarioService.create({
            ...dependenteParaEnvio,
            pesquisa_id: pesquisaId,
            sessao_id: sessaoCriada.id
          });
          
          if (!dependente || !dependente.id) {
            throw new Error(`Falha ao criar dependente ${i}: resposta inválida da API`);
          }
          
          usuariosCriados.push(dependente);
        }
      }
      
      
      saveUsuariosToLocalStorage(usuariosCriados);
      
      if (onUsuarioCriado) {
        onUsuarioCriado(usuarioTitular);
      }
      
      if (onNext) {
        onNext();
      }
      
    } catch (error: any) {
      console.error('Erro ao criar sessão e usuário:', error);
      
      let errorMessage = 'Erro ao criar sessão e usuário';
      
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setValidationErrors([errorMessage]);
      if (onValidationError) {
        onValidationError([errorMessage]);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    if (field === 'cpf' || field.includes('_cpf')) {
      value = maskCPF(value);
    } else if (field === 'celular' || field.includes('_celular')) {
      value = maskCelular(value);
    } else if (field === 'email' || field.includes('_email')) {
      value = maskEmail(value);
    } else if (field === 'altura' || field.includes('_altura')) {
      value = maskAltura(value);
    }
    
    onChange(field, value);
    
    if (field === 'cpf' && value && pesquisaId) {
      checkCpfDuplicate(value, pesquisaId);
    }
  };

  const checkCpfDuplicate = async (cpf: string, pesquisaId: string) => {
    if (!cpf || cpf.length < 14) return;
    
    if (!validarCPF(cpf)) {
      const error = 'CPF inválido. Verifique os dígitos.';
      setValidationErrors([error]);
      if (onValidationError) {
        onValidationError([error]);
      }
      if (onBlockNavigation) {
        onBlockNavigation(true);
      }
      return;
    }
    
    setIsCheckingCpf(true);
    try {
      try {
        const sessaoPendente = await sessaoService.getByCpfAndPesquisa(cpf, pesquisaId);
        
        if (sessaoPendente && sessaoPendente.status === 'pendente') {
          setValidationErrors([]);
          if (onValidationError) {
            onValidationError([]);
          }
          if (onBlockNavigation) {
            onBlockNavigation(false);
          }
          if (onSessaoEncontrada) {
            onSessaoEncontrada(sessaoPendente);
          }
          return;
        }
      } catch (sessaoError) {
      }
      
      const usuarioExistente = await usuarioService.getByCpfAndPesquisa(cpf, pesquisaId);
      
      if (usuarioExistente && Array.isArray(usuarioExistente) && usuarioExistente.length > 0) {
        try {
          const sessaoAtiva = await sessaoService.getByCpfAndPesquisa(cpf, pesquisaId);
          if (sessaoAtiva && sessaoAtiva.status === 'ativo') {
            const error = 'Este CPF já respondeu esta pesquisa. Cada pessoa só pode responder uma vez.';
            setValidationErrors([error]);
            if (onValidationError) {
              onValidationError([error]);
            }
            if (onBlockNavigation) {
              onBlockNavigation(true);
            }
            return;
          }
        } catch (sessaoError) {
          const error = 'Este CPF já respondeu esta pesquisa. Cada pessoa só pode responder uma vez.';
          setValidationErrors([error]);
          if (onValidationError) {
            onValidationError([error]);
          }
          if (onBlockNavigation) {
            onBlockNavigation(true);
          }
          return;
        }
      }
      
      setValidationErrors([]);
      if (onValidationError) {
        onValidationError([]);
      }
      if (onBlockNavigation) {
        onBlockNavigation(false);
      }
    } catch (error) {
      setValidationErrors([]);
      if (onValidationError) {
        onValidationError([]);
      }
      if (onBlockNavigation) {
        onBlockNavigation(false);
      }
    } finally {
      setIsCheckingCpf(false);
    }
  };

  const handleRadioChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange(field, value);
  };

  return (
    <Container>
      <Title>{mapeamentoAnonimizado ? 'Informações do Questionário' : 'Dados Pessoais'}</Title>
      
      {isCheckingCpf && (
        <div style={{
          background: '#d1ecf1',
          color: '#0c5460',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #bee5eb'
        }}>
          Verificando CPF...
        </div>
      )}

      {validationErrors.length > 0 && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}
      
      {!somenteTitulares && (
        <div style={{ marginBottom: '40px', padding: '20px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
          <Subtitle>Número de Dependentes</Subtitle>
          <Note>
            {mapeamentoAnonimizado 
              ? 'Informe quantos dependentes você possui. As perguntas de saúde serão aplicadas para você e seus dependentes de forma anônima.'
              : 'Informe quantos dependentes você possui. As perguntas de saúde serão aplicadas para você e seus dependentes.'
            }
          </Note>
          <Input 
            type="number" 
            value={data.numero_dependentes || ''} 
            onChange={handleInputChange('numero_dependentes')}
            placeholder="0"
            min="0"
            max="10"
          />
        </div>
      )}

      <Form>
        {!mapeamentoAnonimizado && (
          <>
            <InputGroup>
              <Subtitle>Nome completo</Subtitle>
              <Input 
                type="text" 
                value={data.nome || ''} 
                onChange={handleInputChange('nome')}
                placeholder="Digite seu nome completo"
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>CPF</Subtitle>
              <Input 
                type="text" 
                value={data.cpf || ''} 
                onChange={handleInputChange('cpf')}
                placeholder="000.000.000-00"
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>E-mail</Subtitle>
              <Input 
                type="email" 
                value={data.email || ''} 
                onChange={handleInputChange('email')}
                placeholder="seu@email.com"
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>Celular</Subtitle>
              <Input 
                type="tel" 
                value={data.celular || ''} 
                onChange={handleInputChange('celular')}
                placeholder="(00) 00000-0000"
              />
            </InputGroup>
          </>
        )}

        <InputGroup>
          <Subtitle>Sexo biológico</Subtitle>
          <RadioGroup>
            <Input 
              type="radio" 
              id="sexo_masculino" 
              name="sexo" 
              value="Masculino" 
              checked={data.sexo === 'Masculino'}
              onChange={handleRadioChange('sexo')}
            />
            <Label htmlFor="sexo_masculino">Masculino</Label>
          </RadioGroup>
          <RadioGroup>
            <Input 
              type="radio" 
              id="sexo_feminino" 
              name="sexo" 
              value="Feminino" 
              checked={data.sexo === 'Feminino'}
              onChange={handleRadioChange('sexo')}
            />
            <Label htmlFor="sexo_feminino">Feminino</Label>
          </RadioGroup>
        </InputGroup>

        <InputGroup>
          <Subtitle>Data de nascimento</Subtitle>
          <Input 
            type="date" 
            value={data.nascimento || ''} 
            onChange={handleInputChange('nascimento')}
          />
        </InputGroup>

        {!isDass21 && (
          <>
            <InputGroup>
              <Note>
                Nota: Digite seu Peso ou valor aproximado (kg) Coloque valores
                inteiros (Ex: 78)
                <br />
                Caso não saiba o seu peso exato, não tem problema, informe um
                valor aproximado, ok?
              </Note>
            </InputGroup>

            <InputGroup>
              <Subtitle>Peso (kg)</Subtitle>
              <Input 
                type="number" 
                value={data.peso || ''} 
                onChange={handleInputChange('peso')}
                placeholder="70"
                min="0"
                max="400"
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>Altura (m)</Subtitle>
              <Input 
                type="text" 
                value={data.altura || ''} 
                onChange={handleInputChange('altura')}
                placeholder="1.70"
              />
            </InputGroup>
          </>
        )}
      </Form>

      {data.numero_dependentes > 0 && (
        <>
          {Array.from({ length: data.numero_dependentes }, (_, index) => (
            <div key={index} style={{ 
              marginTop: '40px', 
              padding: '30px', 
              background: '#ffffff', 
              borderRadius: '12px', 
              border: '2px solid #e9ecef',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}>
              <div style={{ 
                color: '#222222', 
                fontSize: '20px', 
                fontWeight: '600', 
                marginBottom: '25px',
                paddingBottom: '10px',
                borderBottom: '2px solid #e9ecef'
              }}>
                Dados do Dependente {index + 1}
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {!mapeamentoAnonimizado && (
                  <>
                    <div>
                      <div style={{ 
                        marginBottom: '8px', 
                        color: '#495057', 
                        fontSize: '14px', 
                        fontWeight: '600' 
                      }}>
                        Nome completo
                      </div>
                      <Input 
                        type="text" 
                        value={data[`dependente_${index + 1}_nome`] || ''} 
                        onChange={handleInputChange(`dependente_${index + 1}_nome`)}
                        placeholder="Digite o nome completo do dependente"
                      />
                    </div>

                    <div>
                      <div style={{ 
                        marginBottom: '8px', 
                        color: '#495057', 
                        fontSize: '14px', 
                        fontWeight: '600' 
                      }}>
                        CPF
                      </div>
                      <Input 
                        type="text" 
                        value={data[`dependente_${index + 1}_cpf`] || ''} 
                        onChange={handleInputChange(`dependente_${index + 1}_cpf`)}
                        placeholder="000.000.000-00"
                      />
                    </div>
                  </>
                )}

                <div>
                  <div style={{ 
                    marginBottom: '8px', 
                    color: '#495057', 
                    fontSize: '14px', 
                    fontWeight: '600' 
                  }}>
                    Sexo
                  </div>
                  <RadioGroup>
                    <Label>
                      <input
                        type="radio"
                        name={`dependente_${index + 1}_sexo`}
                        value="Masculino"
                        checked={data[`dependente_${index + 1}_sexo`] === 'Masculino'}
                        onChange={handleRadioChange(`dependente_${index + 1}_sexo`)}
                      />
                      Masculino
                    </Label>
                    <Label>
                      <input
                        type="radio"
                        name={`dependente_${index + 1}_sexo`}
                        value="Feminino"
                        checked={data[`dependente_${index + 1}_sexo`] === 'Feminino'}
                        onChange={handleRadioChange(`dependente_${index + 1}_sexo`)}
                      />
                      Feminino
                    </Label>
                  </RadioGroup>
                </div>

                <div>
                  <div style={{ 
                    marginBottom: '8px', 
                    color: '#495057', 
                    fontSize: '14px', 
                    fontWeight: '600' 
                  }}>
                    Relacionamento
                  </div>
                  <RadioGroup>
                    <Label>
                      <input
                        type="radio"
                        name={`dependente_${index + 1}_relacionamento`}
                        value="filho"
                        checked={data[`dependente_${index + 1}_relacionamento`] === 'filho'}
                        onChange={handleRadioChange(`dependente_${index + 1}_relacionamento`)}
                      />
                      Filho(a)
                    </Label>
                    <Label>
                      <input
                        type="radio"
                        name={`dependente_${index + 1}_relacionamento`}
                        value="conjuge"
                        checked={data[`dependente_${index + 1}_relacionamento`] === 'conjuge'}
                        onChange={handleRadioChange(`dependente_${index + 1}_relacionamento`)}
                      />
                      Cônjuge
                    </Label>
                  </RadioGroup>
                </div>

                <div>
                  <div style={{ 
                    marginBottom: '8px', 
                    color: '#495057', 
                    fontSize: '14px', 
                    fontWeight: '600' 
                  }}>
                    Data de Nascimento
                  </div>
                  <Input 
                    type="date" 
                    value={data[`dependente_${index + 1}_nascimento`] || ''} 
                    onChange={handleInputChange(`dependente_${index + 1}_nascimento`)}
                  />
                </div>

                {!isDass21 && (
                  <>
                    <div>
                      <div style={{ 
                        marginBottom: '8px', 
                        color: '#495057', 
                        fontSize: '14px', 
                        fontWeight: '600' 
                      }}>
                        Peso (kg)
                      </div>
                      <Input 
                        type="number" 
                        value={data[`dependente_${index + 1}_peso`] || ''} 
                        onChange={handleInputChange(`dependente_${index + 1}_peso`)}
                        placeholder="70"
                        min="0"
                        max="400"
                      />
                    </div>

                    <div>
                      <div style={{ 
                        marginBottom: '8px', 
                        color: '#495057', 
                        fontSize: '14px', 
                        fontWeight: '600' 
                      }}>
                        Altura (m)
                      </div>
                      <Input 
                        type="text" 
                        value={data[`dependente_${index + 1}_altura`] || ''} 
                        onChange={handleInputChange(`dependente_${index + 1}_altura`)}
                        placeholder="1.70"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {mapeamentoAnonimizado && (
        <div style={{ 
          marginTop: '40px', 
          padding: '30px', 
          background: '#e8f4fd', 
          borderRadius: '12px', 
          border: '2px solid #b8daff',
          textAlign: 'center'
        }}>
          <div style={{ 
            color: '#0c5460', 
            fontSize: '18px', 
            fontWeight: '600', 
            marginBottom: '15px'
          }}>
            Mapeamento Anônimo
          </div>
          <div style={{ 
            color: '#0c5460', 
            fontSize: '14px', 
            lineHeight: '1.6'
          }}>
            Este questionário é anônimo. Não coletamos dados de identificação, apenas informações de saúde necessárias para a pesquisa.
          </div>
        </div>
      )}

      <div style={{ 
        display: 'flex',
        justifyContent: 'flex-end',
        margin: '40px 20px',
        gap: '20px',
        padding: '30px 0',
        borderTop: '1px solid #e9ecef'
      }}>
        <button
          onClick={handleCreateSessionAndUser}
          disabled={isCreating}
          style={{
            backgroundColor: '#222222',
            color: '#fff',
            border: 'none',
            padding: '14px 28px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isCreating ? 'not-allowed' : 'pointer',
            opacity: isCreating ? 0.6 : 1,
            transition: 'all 0.2s ease',
            minWidth: '140px'
          }}
        >
          {isCreating ? 'Criando...' : 'Continuar'}
        </button>
      </div>
    </Container>
  );
};

export default DadosPessoais;